import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../../../../services/api';
import { tariffsApi } from '../../../../services/api/tariffs';
import { EEGTariff, EEGTariffUpdate, MarktpreisTariff, MarktpreisTariffUpdate, PPATariff, PPATariffCreate, PPATariffUpdate, EnergyRevenuesResponse, Ertragsprognose, AktiverErtragswert, ValidationsResponse } from '../../../../types';
import { hasFieldChanged, hasAnyFieldChanged } from '../../../../utils/formUtils';

interface UseTariffsDataProps {
    projectId: string;
    onSaveSuccess?: () => void;
    onSaveError?: (error: string) => void;
    /** Callback wenn Validierungen aus der API empfangen werden */
    onValidationsReceived?: (validations: ValidationsResponse) => void;
}

/**
 * Helper: Liest den richtigen Ertragswert aus der Ertragsprognose basierend auf dem aktiven Typ
 */
export const getErtragswertFromPrognose = (
    forecast: Ertragsprognose | null,
    typ: AktiverErtragswert | undefined
): number | undefined => {
    if (!forecast) return undefined;
    switch (typ) {
        case 'Bruttoertrag': return forecast.bruttoertrag;
        case 'P50': return forecast.nettoertrag_p50;
        case 'P75': return forecast.nettoertrag_p75;
        case 'P90': return forecast.nettoertrag_p90;
        default: return forecast.nettoertrag_p75; // Default: P75
    }
};

interface UseTariffsDataReturn {
    eegTariff: EEGTariff | null;
    marktpreisTariff: MarktpreisTariff | null;
    ppaTariffs: PPATariff[];
    activeYieldForecast: Ertragsprognose | null;
    isLoading: boolean;
    error: string | null;
    isSaving: boolean;
    updateEEGField: (field: keyof EEGTariffUpdate, value: number | undefined) => void;
    updateMarktpreisField: (field: keyof MarktpreisTariffUpdate, value: number | undefined) => void;
    createPPATariff: (data: PPATariffCreate) => Promise<PPATariff>;
    updatePPATariff: (tariffId: string, data: PPATariffUpdate) => Promise<PPATariff>;
    deletePPATariff: (tariffId: string) => Promise<void>;
    /** Holt die Backend-berechneten Energieerlöse via GET (für Aktualisieren-Button) */
    refreshBackendRevenues: () => Promise<EnergyRevenuesResponse | null>;
    reload: () => Promise<void>;
}

/**
 * Hook für das Laden und Aktualisieren von Tariff-Daten (EEG und PPA)
 * Speichert bei Blur (kein Debouncing mehr nötig, da InputFieldNumber nur bei Blur triggert)
 * 
 * Die PPA-CRUD-Operationen geben jetzt Validierungen zurück, die über
 * onValidationsReceived an den Parent weitergegeben werden können.
 */
export const useTariffsData = ({ projectId, onSaveSuccess, onSaveError, onValidationsReceived }: UseTariffsDataProps): UseTariffsDataReturn => {
    const [eegTariff, setEegTariff] = useState<EEGTariff | null>(null);
    const [marktpreisTariff, setMarktpreisTariff] = useState<MarktpreisTariff | null>(null);
    const [ppaTariffs, setPpaTariffs] = useState<PPATariff[]>([]);
    const [activeYieldForecast, setActiveYieldForecast] = useState<Ertragsprognose | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Stabile Referenzen für Callbacks um unnötige Re-Renders zu vermeiden
    const onSaveSuccessRef = useRef(onSaveSuccess);
    const onSaveErrorRef = useRef(onSaveError);
    const onValidationsReceivedRef = useRef(onValidationsReceived);

    // Callbacks aktualisieren wenn sie sich ändern
    useEffect(() => {
        onSaveSuccessRef.current = onSaveSuccess;
    }, [onSaveSuccess]);

    useEffect(() => {
        onSaveErrorRef.current = onSaveError;
    }, [onSaveError]);

    useEffect(() => {
        onValidationsReceivedRef.current = onValidationsReceived;
    }, [onValidationsReceived]);

    /**
     * Verarbeitet Validierungen aus einer API-Response.
     * Leitet die Validierungen an den PageWarningsContext weiter (via Callback).
     */
    const handleValidations = useCallback((validations: ValidationsResponse) => {
        onValidationsReceivedRef.current?.(validations);
    }, []);

    // Lade Tariff-Daten (alle 4 API-Calls parallel via Promise.allSettled)
    const loadTariffs = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            const [eegResult, marktpreisResult, ppaResult, prognoseResult] = await Promise.allSettled([
                apiService.getEEGTariff(projectId),
                apiService.getMarktpreisTariff(projectId),
                apiService.getPPATariffs(projectId),
                apiService.getActiveProjectErtragsprognose(projectId),
            ]);

            // Ergebnisse auswerten – bei rejected wird der Default-Wert verwendet
            const eeg = eegResult.status === 'fulfilled' ? eegResult.value : null;
            const marktpreis = marktpreisResult.status === 'fulfilled' ? marktpreisResult.value : null;
            const ppa = ppaResult.status === 'fulfilled' ? ppaResult.value : [];
            const activePrognose = prognoseResult.status === 'fulfilled' ? prognoseResult.value : null;

            setEegTariff(eeg);
            setMarktpreisTariff(marktpreis);
            setPpaTariffs(ppa);
            setActiveYieldForecast(activePrognose);
        } catch {
        } finally {
            setIsLoading(false);
        }
    }, [projectId]);

    // Initial laden
    useEffect(() => {
        loadTariffs();
    }, [loadTariffs]);

    // EEG-Feld aktualisieren und sofort speichern
    const updateEEGField = useCallback(async (field: keyof EEGTariffUpdate, value: number | undefined) => {
        if (!hasFieldChanged(eegTariff, field as string, value)) return;

        // Lokalen State sofort aktualisieren
        setEegTariff(prev => {
            if (!prev) {
                // Neuen EEG-Tarif erstellen wenn noch keiner existiert
                return {
                    id: '',
                    project_id: projectId,
                    [field]: value,
                    created_at: new Date().toISOString()
                } as EEGTariff;
            }
            return { ...prev, [field]: value };
        });

        // Sofort speichern
        try {
            setIsSaving(true);
            const updated = await apiService.createOrUpdateEEGTariff(projectId, { [field]: value });
            setEegTariff(updated);
            onSaveSuccessRef.current?.();
            // Chart-Daten werden automatisch im Frontend neu berechnet (useComputedRevenues)
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Fehler beim Speichern';
            setError(errorMsg);
            onSaveErrorRef.current?.(errorMsg);
        } finally {
            setIsSaving(false);
        }
    }, [projectId, eegTariff]);

    // Marktpreis-Feld aktualisieren und sofort speichern
    const updateMarktpreisField = useCallback(async (field: keyof MarktpreisTariffUpdate, value: number | undefined) => {
        if (!hasFieldChanged(marktpreisTariff, field as string, value)) return;

        // Lokalen State sofort aktualisieren
        setMarktpreisTariff(prev => {
            if (!prev) {
                return {
                    id: '',
                    project_id: projectId,
                    marktpreis_kwh: 6.0,
                    created_at: new Date().toISOString(),
                    [field]: value,
                };
            }
            return { ...prev, [field]: value };
        });

        // Sofort speichern
        try {
            setIsSaving(true);
            const updated = await apiService.createOrUpdateMarktpreisTariff(projectId, { [field]: value });
            setMarktpreisTariff(updated);
            onSaveSuccessRef.current?.();
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Fehler beim Speichern';
            setError(errorMsg);
            onSaveErrorRef.current?.(errorMsg);
        } finally {
            setIsSaving(false);
        }
    }, [projectId, marktpreisTariff]);

    // PPA-Tarif erstellen
    // Verwendet die neue API die Validierungen zurückgibt
    const createPPATariff = useCallback(async (data: PPATariffCreate): Promise<PPATariff> => {
        try {
            setIsSaving(true);
            const response = await tariffsApi.createPPATariff(projectId, data);

            // Extrahiere den PPA aus der Response
            setPpaTariffs(prev => [...prev, response.data]);

            // Verarbeite Validierungen aus der Response
            handleValidations(response.validations);
            // Chart-Daten werden automatisch im Frontend neu berechnet (useComputedRevenues)

            return response.data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fehler beim Erstellen');
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [projectId, handleValidations]);

    // PPA-Tarif aktualisieren
    // Verwendet die neue API die Validierungen zurückgibt
    const updatePPATariff = useCallback(async (tariffId: string, data: PPATariffUpdate): Promise<PPATariff> => {
        const currentTariff = ppaTariffs.find(t => t.id === tariffId);
        if (currentTariff && !hasAnyFieldChanged(currentTariff, data)) {
            return currentTariff;
        }

        try {
            setIsSaving(true);
            const response = await tariffsApi.updatePPATariff(projectId, tariffId, data);

            // Extrahiere den PPA aus der Response
            setPpaTariffs(prev => prev.map(t => t.id === tariffId ? response.data : t));

            // Verarbeite Validierungen aus der Response
            handleValidations(response.validations);
            // Chart-Daten werden automatisch im Frontend neu berechnet (useComputedRevenues)

            return response.data;
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fehler beim Aktualisieren');
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [projectId, ppaTariffs, handleValidations]);

    // PPA-Tarif löschen
    // Verwendet die neue API die Validierungen zurückgibt
    const deletePPATariff = useCallback(async (tariffId: string): Promise<void> => {
        try {
            setIsSaving(true);
            const response = await tariffsApi.deletePPATariff(projectId, tariffId);

            // PPA aus State entfernen
            setPpaTariffs(prev => prev.filter(t => t.id !== tariffId));
            onSaveSuccessRef.current?.();

            // Verarbeite Validierungen aus der Response
            handleValidations(response.validations);
            // Chart-Daten werden automatisch im Frontend neu berechnet (useComputedRevenues)
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Fehler beim Löschen';
            setError(errorMsg);
            onSaveErrorRef.current?.(errorMsg);
            throw err;
        } finally {
            setIsSaving(false);
        }
    }, [projectId, handleValidations]);

    /**
     * Holt die Backend-berechneten Energieerlöse via GET.
     * Wird vom Aktualisieren-Button in der RevenuesPerYearSection verwendet.
     */
    const refreshBackendRevenues = useCallback(async (): Promise<EnergyRevenuesResponse | null> => {
        const result = await apiService.getEnergyRevenues(projectId);
        return result;
    }, [projectId]);

    return {
        eegTariff,
        marktpreisTariff,
        ppaTariffs,
        activeYieldForecast,
        isLoading,
        error,
        isSaving,
        updateEEGField,
        updateMarktpreisField,
        createPPATariff,
        updatePPATariff,
        deletePPATariff,
        refreshBackendRevenues,
        reload: loadTariffs
    };
};
